import React, { useEffect, useState } from 'react';
import { Star, BadgeCheck } from 'lucide-react';
import { listReviews, reviewsSummary, submitReview, historicalFeedback } from '../api';
import { useToast } from '../hooks/use-toast';

const Stars = ({ n, size = 'h-4 w-4' }) => (
  <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => (
    <Star key={i} className={`${size} ${i <= n ? 'text-[#f0d582] fill-[#f0d582]' : 'text-[#f5ecd9]/25'}`} />
  ))}</span>
);

export const ProductReviews = ({ productId = null, showForm = false, showHistorical = false }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [historical, setHistorical] = useState([]);
  const [form, setForm] = useState({ rating: 5, body: '', display_name: '' });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const authed = !!localStorage.getItem('pe_cust_token');

  const load = async () => {
    setReviews(await listReviews(productId));
    setSummary(await reviewsSummary(productId));
    if (showHistorical) { try { setHistorical(await historicalFeedback(productId)); } catch { /* */ } }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) return;
    setBusy(true);
    try {
      const r = await submitReview({ product_id: productId, rating: form.rating, body: form.body, display_name: form.display_name });
      toast({ title: 'Review submitted', description: r.message || 'Pending approval.' });
      setForm({ rating: 5, body: '', display_name: '' });
    } catch (er) { toast({ title: 'Could not submit', description: er?.response?.data?.detail || 'Try again.' }); }
    setBusy(false);
  };

  return (
    <div data-testid="product-reviews">
      {showHistorical && historical.length > 0 && (
        <div className="mb-14" data-testid="historical-feedback">
          <div className="text-center mb-2"><h3 className="font-cinzel text-2xl text-[#f0d582]">Loved by Our Community Since 2019</h3></div>
          <p className="text-center font-jost text-xs text-[#f5ecd9]/45 max-w-2xl mx-auto mb-8">Historical feedback from our community over the years, before our online review system launched. These are not tied to online orders.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {historical.map((h, i) => (
              <div key={i} data-testid={`historical-${i}`} className="rounded-xl p-5 border border-[#d4af37]/15 bg-white/[0.02]">
                <p className="font-cormorant text-lg text-[#f5ecd9]/80 leading-relaxed italic">"{h.body}"</p>
                {h.product_name && <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/35 mt-3">On {h.product_name}</p>}
              </div>
            ))}
          </div>
          <div className="gold-divider mt-12" />
          <h3 className="font-cinzel text-2xl text-[#f0d582] text-center mt-10 mb-2">Verified Customer Reviews</h3>
          <p className="text-center font-jost text-xs text-[#f5ecd9]/45 mb-6">Reviews submitted by customers whose purchases were confirmed through our online store.</p>
        </div>
      )}
      {summary && summary.count > 0 && (
        <div className="flex items-center gap-3 mb-6">
          <span className="font-cinzel text-3xl text-gold-gradient">{summary.average}</span>
          <div><Stars n={Math.round(summary.average)} /><p className="font-jost text-xs text-[#f5ecd9]/50 mt-1">{summary.count} review{summary.count === 1 ? '' : 's'}</p></div>
        </div>
      )}

      {showForm && productId && (
        authed ? (
          <form onSubmit={submit} className="card-glass rounded-2xl p-5 mb-8 space-y-3" data-testid="review-form">
            <div className="flex items-center gap-2"><span className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">Your rating</span>
              {[1, 2, 3, 4, 5].map((i) => (<button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`star-${i}`}><Star className={`h-5 w-5 ${i <= form.rating ? 'text-[#f0d582] fill-[#f0d582]' : 'text-[#f5ecd9]/25'}`} /></button>))}
            </div>
            <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Display name (optional)" className="w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-2.5 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6]" />
            <textarea rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share your experience…" data-testid="review-body" className="w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-2.5 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] resize-none" />
            <button type="submit" disabled={busy} data-testid="review-submit" className="btn-gold rounded-full px-6 py-2.5 text-xs font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582] disabled:opacity-60">{busy ? 'Sending…' : 'Submit Review'}</button>
            <p className="font-jost text-[10px] text-[#f5ecd9]/40">Reviews are moderated before publishing.</p>
          </form>
        ) : (
          <p className="font-jost text-xs text-[#f5ecd9]/50 mb-6"><a href="/account" className="text-[#f0d582] underline">Sign in</a> to leave a review.</p>
        )
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {reviews.length === 0 && <p className="font-cormorant text-lg text-[#f5ecd9]/50 md:col-span-2 text-center py-6">Verified reviews are coming soon. As customers shop our newly launched online store, verified reviews will appear here.</p>}
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className="card-glass rounded-xl p-5">
            <div className="flex items-center justify-between gap-2">
              <Stars n={r.rating} />
              {r.verified_purchase && <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#7bb661]"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
            </div>
            <p className="font-cormorant text-lg text-[#f5ecd9]/85 mt-3 leading-relaxed">{r.body}</p>
            <p className="font-jost text-xs text-[#f5ecd9]/45 mt-3">— {r.display_name}{!productId && r.product_name ? ` · ${r.product_name}` : ''} · {new Date(r.created_at).toLocaleDateString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
